import React from "react";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import VehicleType from "@app.automotus.io/components/common/VehicleType";
import { RegisteredVehicle } from "common/graphql/fragments";
import type { Theme } from "@mui/material/styles";

export interface VehicleSelectionCardContentProps {
  /** The vehicle to display */
  vehicle: RegisteredVehicle;
  /** Whether this vehicle is selected */
  isSelected: boolean;
  /** Whether selection is disabled (e.g., when "apply to all" is checked) */
  isDisabled?: boolean;
  /** Handler for when the card is clicked */
  onCardClick: () => void;
  /** Handler for when the checkbox is toggled */
  onCheckboxChange: () => void;
}

/**
 * Presentation component for a selectable vehicle card.
 * Displays vehicle information with a checkbox for selection.
 */
export const VehicleSelectionCardContent: React.FC<VehicleSelectionCardContentProps> = ({
  vehicle,
  isSelected,
  isDisabled = false,
  onCardClick,
  onCheckboxChange,
}) => {
  const vehicleTypeLabel = vehicle.type ? VehicleType.FromValue(vehicle.type)?.label : null;
  const displayName = vehicle.make && vehicle.model ? `${vehicle.make} ${vehicle.model}` : "Unknown Vehicle";

  const handleCheckboxClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isDisabled) {
      onCheckboxChange();
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        flexShrink: 0,
        borderColor: isSelected ? "primary.main" : "divider",
        borderWidth: isSelected ? 2 : 1,
        boxShadow: isSelected ? (theme: Theme) => `0 0 0 2px ${theme.palette.primary.main}20` : "none",
        transition: "all 0.2s ease-in-out",
        opacity: isDisabled && !isSelected ? 0.7 : 1,
      }}
    >
      <CardActionArea onClick={onCardClick} disabled={isDisabled} sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Checkbox checked={isSelected} disabled={isDisabled} onClick={handleCheckboxClick} sx={{ p: 0 }} />
          <DirectionsCarIcon sx={{ color: "text.secondary" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {displayName}
              {vehicleTypeLabel && (
                <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.secondary", fontWeight: 400 }}>
                  ({vehicleTypeLabel})
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              License Plate:{" "}
              <Typography component="span" sx={{ fontFamily: "monospace", fontWeight: 500 }}>
                {vehicle.stateCode} {vehicle.licensePlate}
              </Typography>
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default VehicleSelectionCardContent;
